// Customers — merchant-scoped customer directory (PDF round 2 item 5).
//
// A customer is a per-merchant identity keyed on (email, phone). One
// customer maps to 1..N beneficiary rows (= bank accounts). This page
// lists the directory; clicking a row drills into /customers/:id which
// shows the customer + all their bank accounts.
//
// Visibility: viewer+ on the membership layer. Admin viewing the
// platform-wide overview (no currentMerchantId selected) gets the
// empty-state prompt to pick a merchant first.
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { formatDateTime } from "@/utils/format";
import { listCustomers } from "@/api/customers";
import { useUI } from "@/store/ui";

export function Customers() {
  const navigate = useNavigate();
  const currentMerchantId = useUI((s) => s.currentMerchantId);
  const [search, setSearch] = useState<string>("");
  // The text input is uncontrolled-for-fetch: we hydrate `search` only
  // on Enter / blur so a 200-char query doesn't fire 200 list calls.
  const [pendingSearch, setPendingSearch] = useState<string>("");

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["customers", currentMerchantId, search],
    queryFn: () =>
      currentMerchantId == null
        ? Promise.resolve({ customers: [], limit: 0 })
        : listCustomers(currentMerchantId, {
            q: search || undefined,
            limit: 100,
          }),
    enabled: currentMerchantId != null,
  });

  const customers = data?.customers ?? [];

  if (currentMerchantId == null) {
    return (
      <div className="page">
        <div className="page-head">
          <div>
            <div className="eyebrow">Console · Customers</div>
            <h1>Customers</h1>
          </div>
        </div>
        <div className="card flat">
          <div className="empty">
            <p>
              Pick a merchant in the top-right switcher to see its customer
              directory.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Console · Customers</div>
          <h1>Customers</h1>
          <div className="sub">
            One customer = one (email, phone) pair on this merchant. Each
            customer can have 1 or more bank accounts.
          </div>
        </div>
        <div className="spacer" />
        <button
          className="btn ghost sm"
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="card flat" style={{ marginTop: 12, padding: "12px 16px" }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(pendingSearch.trim());
          }}
          style={{ display: "flex", gap: 8, alignItems: "center" }}
        >
          <input
            className="input"
            type="text"
            placeholder="Search by email, phone, or name…"
            value={pendingSearch}
            onChange={(e) => setPendingSearch(e.target.value)}
            onBlur={() => setSearch(pendingSearch.trim())}
            style={{ flex: 1 }}
          />
          <button className="btn sm" type="submit">
            Search
          </button>
          {search !== "" && (
            <button
              className="btn ghost sm"
              type="button"
              onClick={() => {
                setSearch("");
                setPendingSearch("");
              }}
            >
              Clear
            </button>
          )}
        </form>
      </div>

      <div className="card flat" style={{ marginTop: 12 }}>
        {isLoading ? (
          <div className="empty">
            <p className="dim">Loading customers…</p>
          </div>
        ) : isError ? (
          <div className="empty">
            <p>Could not load customers.</p>
          </div>
        ) : customers.length === 0 ? (
          <div className="empty">
            <p>
              {search !== ""
                ? "No customers match this search."
                : "No customers on this merchant yet. They are auto-created when you register beneficiaries through the panel or CSV upload."}
            </p>
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th style={{ textAlign: "right" }}>Accounts</th>
                <th>Added</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr
                  key={c.id}
                  className="clickable"
                  onClick={() => navigate(`/customers/${c.id}`)}
                >
                  <td>
                    <div className="cell-name">
                      {c.name || <span className="dim">(no name)</span>}
                    </div>
                    <div className="cell-id">cus_{c.id}</div>
                  </td>
                  <td className="mono dim" style={{ fontSize: 12 }}>
                    {c.email}
                  </td>
                  <td className="mono dim" style={{ fontSize: 12 }}>
                    {c.phone}
                  </td>
                  <td
                    className="mono"
                    style={{ textAlign: "right", fontSize: 12 }}
                  >
                    {c.beneficiary_count}
                  </td>
                  <td className="dim" style={{ fontSize: 12 }}>
                    {formatDateTime(c.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div
        className="dim"
        style={{ fontSize: 11, marginTop: 12, lineHeight: 1.5 }}
      >
        <Link to="/beneficiaries" style={{ color: "inherit" }}>
          ← Beneficiaries (bank accounts)
        </Link>{" "}
        — one beneficiary row per (customer, bank account). A customer's
        accounts appear together on the customer detail page.{" "}
        <I.ArrowRight size={10} />
      </div>
    </div>
  );
}
